<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { chatApi } from '@/services/api'
import { useInfinityResource } from '@/services/resource'
import BModal from '@/components/common/BModal.vue'
import BButton from '@/components/common/bButton.vue'
import BOptions from '@/components/common/bOptions.vue'
import BLabel from '@/components/common/bLabel.vue'
import Combobox from '@/components/common/Combobox.vue'
import TextInput from '@/components/common/textInput.vue'
import ListItemResource from '@/components/resource/autocomplete/ListItem.vue'
import MaterialSymbolsCloseRounded from '~icons/material-symbols/close-rounded'

const { t } = useI18n()

const open = defineModel<boolean>({ default: false })

const emits = defineEmits<{
  (e: 'created', conversation: { _id: string }): void
}>()

const typeOptions = [
  { id: 'dm', display: t('modules.chat.directMessage') },
  { id: 'group', display: t('modules.chat.groupMessage') },
]

const selectedType = ref(typeOptions[0])
const createTitle = ref('')
const dmMember = ref<any>()
const groupMembers = ref<any[]>([])
const creating = ref(false)

const {
  getMoreData: getMoreResource,
  haveMore: haveMoreResource,
  limit: limitResource,
  page: pageResource,
  list: listResource,
  search: searchResource,
  isLoading: isLoadingResource,
} = useInfinityResource({})

const createMode = computed(() => String(selectedType.value.id) as 'dm' | 'group')

const resourceOptions = computed(() =>
  listResource.value.map((resource: any) => ({
    ...resource,
    display: resource.display
      || `${resource.firstname || ''} ${resource.lastname || ''}`.trim(),
  })),
)

const canCreateGroup = computed(() =>
  groupMembers.value.length >= 1 && createTitle.value.trim().length > 0 && !creating.value,
)

const resetCreateForm = () => {
  selectedType.value = typeOptions[0]
  createTitle.value = ''
  dmMember.value = undefined
  groupMembers.value = []
  searchResource.value = ''
}

const unlockBodyPointerEvents = () => {
  if (document.body.style.pointerEvents === 'none')
    document.body.style.removeProperty('pointer-events')
}

watch(open, (isOpen) => {
  if (isOpen) {
    resetCreateForm()
    pageResource.value = 0
    getMoreResource()
    return
  }

  // Combobox/Reka can leave body locked after the modal unmounts mid-dropdown.
  nextTick(() => {
    unlockBodyPointerEvents()
    requestAnimationFrame(unlockBodyPointerEvents)
    window.setTimeout(unlockBodyPointerEvents, 200)
  })
})

watch(selectedType, () => {
  dmMember.value = undefined
  groupMembers.value = []
  createTitle.value = ''
  searchResource.value = ''
})

const createConversation = async (memberIds: string[], type: 'dm' | 'group', title?: string) => {
  if (!memberIds.length || creating.value) return
  if (type === 'group' && !title?.trim()) return

  creating.value = true
  try {
    const created = await chatApi.createConversation({
      type,
      title: type === 'group' ? title!.trim() : undefined,
      memberIds,
    }).promise

    open.value = false
    resetCreateForm()
    emits('created', created)
  }
  catch (err) {
    console.error('Failed to create conversation', err)
    if (type === 'dm') dmMember.value = undefined
  }
  finally {
    creating.value = false
  }
}

watch(dmMember, async (member) => {
  if (!open.value || createMode.value !== 'dm' || !member?._id || creating.value) return
  await createConversation([member._id], 'dm')
})

const createGroup = async () => {
  await createConversation(
    groupMembers.value.map(m => m._id),
    'group',
    createTitle.value,
  )
}
</script>

<template>
  <BModal
    v-model="open"
    data-cy="chat-create-conversation-modal"
  >
    <template #header>
      <h2
        class="font-semibold"
        data-cy="chat-create-conversation-title"
      >
        {{ t('modules.chat.newConversation') }}
      </h2>
    </template>

    <div class="space-y-4 p-1">
      <div class="flex flex-col gap-1.5">
        <span class="subtitle text-sm">{{ t('modules.chat.conversationType') }}</span>
        <BOptions
          v-model="selectedType"
          data-cy="chat-conversation-type"
          :list-items="typeOptions"
          size="small"
          class="w-fit"
        />
      </div>

      <TextInput
        v-if="createMode === 'group'"
        v-model="createTitle"
        data-cy="chat-group-name-input"
        :label="t('modules.chat.groupName')"
        :placeholder="t('modules.chat.groupNamePlaceholder')"
      />

      <Combobox
        v-if="createMode === 'dm'"
        v-model="dmMember"
        v-model:search="searchResource"
        v-model:limit="limitResource"
        v-model:page="pageResource"
        v-model:have-more="haveMoreResource"
        data-cy="chat-dm-member-combobox"
        :loading="isLoadingResource || creating"
        :options="resourceOptions"
        :label="t('modules.chat.addMembers')"
        :placeholder="t('modules.chat.searchPeople')"
        close-after
        @search="getMoreResource"
      >
        <template #custom-item="{ item }">
          <ListItemResource :resource="item" />
        </template>
        <template #selected-render="{ selected: person }">
          <ListItemResource :resource="person" />
        </template>
      </Combobox>

      <Combobox
        v-else
        v-model="groupMembers"
        v-model:search="searchResource"
        v-model:limit="limitResource"
        v-model:page="pageResource"
        v-model:have-more="haveMoreResource"
        data-cy="chat-group-members-combobox"
        :loading="isLoadingResource"
        :options="resourceOptions"
        :label="t('modules.chat.addMembers')"
        :placeholder="t('modules.chat.searchPeople')"
        @search="getMoreResource"
      >
        <template #custom-item="{ item }">
          <ListItemResource :resource="item" />
        </template>
        <template #selected-item="{ selected: person, removeSelected }">
          <BLabel
            bg-color="primary-op"
            size="dense"
            class="max-w-full"
          >
            <ListItemResource :resource="person" />
            <button
              type="button"
              class="shrink-0 p-0.5 rounded-full opacity-60 hover:opacity-100 hover:bg-danger-500/10"
              :aria-label="t('common.a11y.removeItem', { item: person.display })"
              @click="removeSelected"
            >
              <MaterialSymbolsCloseRounded class="w-3.5 h-3.5" />
            </button>
          </BLabel>
        </template>
      </Combobox>

      <p
        v-if="createMode === 'dm'"
        class="text-xs opacity-60"
      >
        {{ t('modules.chat.directMessageHint') }}
      </p>
    </div>

    <template
      v-if="createMode === 'group'"
      #footer
    >
      <div class="flex justify-end gap-2">
        <BButton
          bg="base"
          data-cy="chat-create-cancel"
          @click="open = false"
        >
          {{ t('common.cancel') }}
        </BButton>
        <BButton
          bg="primary"
          data-cy="chat-create-group-submit"
          :disabled="!canCreateGroup"
          :is-loading="creating"
          @click="createGroup"
        >
          {{ t('modules.chat.create') }}
        </BButton>
      </div>
    </template>
  </BModal>
</template>
